import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { preflightVideo, formatDuration, dataUrlToFile } from "../lib/preflight";
import { toast } from "sonner";
import { Upload, Film, Image as ImageIcon, ArrowLeft, Cloud, Check, Loader2, AlertCircle, ExternalLink, Sparkles } from "lucide-react";
import * as tus from "tus-js-client";

const MAX_VIDEO_GB = 5;
const MAX_POSTER_MB = 10;
const MAX_GENRES = 3;

export default function UploadMovie() {
  const nav = useNavigate();
  const [hasChannel, setHasChannel] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [genreCatalog, setGenreCatalog] = useState([]);
  const [poster, setPoster] = useState(null);
  const [posterSource, setPosterSource] = useState(""); // "" | "uploaded" | "frame"
  const [video, setVideo] = useState(null);
  const [videoCheck, setVideoCheck] = useState({ phase: "idle" }); // idle | checking | ok | error
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tusUpload, setTusUpload] = useState(null);
  const [seriesList, setSeriesList] = useState([]);
  const [seriesId, setSeriesId] = useState("");
  const [season, setSeason] = useState(1);
  const [episode, setEpisode] = useState(1);
  const [episodeTitle, setEpisodeTitle] = useState("");

  useEffect(() => {
    api.get("/channels/me").then(({ data }) => setHasChannel(!!data)).catch(() => setHasChannel(false));
    api.get("/series?mine=true").then(({ data }) => setSeriesList(data || [])).catch(() => setSeriesList([]));
    api.get("/genres").then(({ data }) => {
      const list = data || [];
      setGenreCatalog(list);
      // Default-select Drama if present, otherwise first available
      if (list.length > 0) {
        const def = list.find((g) => g.name === "Drama") || list[0];
        setSelectedGenres([def.name]);
      }
    }).catch(() => setGenreCatalog([]));
  }, []);

  const toggleGenre = (name) => {
    setSelectedGenres((prev) => {
      if (prev.includes(name)) return prev.filter((g) => g !== name);
      if (prev.length >= MAX_GENRES) {
        toast.error(`You can pick up to ${MAX_GENRES} genres.`);
        return prev;
      }
      return [...prev, name];
    });
  };

  // Auto-prompt resume: surface any incomplete uploads as soon as the page loads.
  useEffect(() => {
    if (hasChannel !== true) return;
    api.get("/movies/mine").then(({ data }) => {
      const incomplete = (data || []).filter((m) => m.status === "pending_upload");
      if (incomplete.length > 0) {
        const latest = incomplete[0]; // already sorted newest first server-side
        toast.message(
          `You have ${incomplete.length} incomplete upload${incomplete.length > 1 ? "s" : ""}`,
          {
            description: `“${latest.title}” is paused. Resume to continue from where you left off.`,
            duration: 12000,
            action: { label: "Resume", onClick: () => nav(`/creator/resume/${latest.id}`) },
          }
        );
      }
    }).catch(() => { /* non-fatal */ });
  }, [hasChannel, nav]);

  const onVideoSelected = async (file) => {
    setVideo(file);
    if (!file) {
      setVideoCheck({ phase: "idle" });
      return;
    }
    if (file.size > MAX_VIDEO_GB * 1024 ** 3) {
      setVideoCheck({ phase: "error", error: `Video is ${(file.size / 1024 ** 3).toFixed(2)} GB — limit is ${MAX_VIDEO_GB} GB.` });
      return;
    }
    setVideoCheck({ phase: "checking" });
    const result = await preflightVideo(file);
    if (result.ok) {
      setVideoCheck({ phase: "ok", ...result });
    } else {
      setVideoCheck({ phase: "error", error: result.error, helpUrl: result.helpUrl });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!poster || !video) return toast.error("Please attach both a poster and a video file");
    if (selectedGenres.length === 0) return toast.error("Pick at least one genre.");
    if (videoCheck.phase !== "ok") {
      return toast.error(videoCheck.error || "Please wait for the video preflight to finish before uploading.");
    }
    if (poster.size / (1024 ** 2) > MAX_POSTER_MB) {
      return toast.error(`Poster is ${(poster.size / 1024 ** 2).toFixed(1)} MB — limit is ${MAX_POSTER_MB} MB.`);
    }

    setSubmitting(true);
    setProgress(0);
    setPhase("initializing");

    let initData;
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("description", description);
      fd.append("genre", selectedGenres[0] || "Drama");
      fd.append("genres", selectedGenres.join(","));
      fd.append("duration_seconds", String(Math.round(videoCheck.duration || 0)));
      fd.append("video_size_bytes", String(video.size));
      if (seriesId) {
        fd.append("series_id", seriesId);
        fd.append("season", String(season || 1));
        fd.append("episode", String(episode || 1));
        if (episodeTitle.trim()) fd.append("episode_title", episodeTitle.trim());
      }
      fd.append("poster", poster);
      const { data } = await api.post("/movies/init", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      initData = data;
    } catch (err) {
      setSubmitting(false);
      setPhase("");
      return toast.error(err.response?.data?.detail || "Could not start upload", { duration: 8000 });
    }

    setPhase("uploading");
    const upload = new tus.Upload(video, {
      endpoint: initData.upload_url,
      uploadUrl: initData.upload_url,
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      metadata: { filename: video.name, filetype: video.type },
      onError: (error) => {
        console.error("tus upload error:", error);
        toast.error(`Upload failed: ${error?.message || "network error"}. Your progress is saved — try again to resume.`, { duration: 10000 });
        setSubmitting(false);
        setPhase("");
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        setProgress(Math.round((bytesUploaded / bytesTotal) * 100));
      },
      onSuccess: async () => {
        setProgress(100);
        setPhase("processing");
        try { await api.post(`/movies/${initData.movie.id}/upload-started`); } catch { /* non-fatal */ }
        toast.success("Upload complete! Cloudflare is transcoding — you'll see it under 'Pending review' shortly.", { duration: 8000 });
        setTimeout(() => nav("/creator"), 1500);
      },
    });

    setTusUpload(upload);
    upload.start();
  };

  const cancelUpload = () => {
    if (tusUpload) {
      tusUpload.abort();
      setTusUpload(null);
    }
    setSubmitting(false);
    setPhase("");
    setProgress(0);
  };

  if (hasChannel === false) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="needs-channel">
        <Film className="h-12 w-12 mx-auto text-[#E65F2B] mb-4" />
        <h2 className="font-display text-3xl font-bold">Set up your channel first</h2>
        <p className="text-white/50 mt-3">You need to create a channel before uploading movies.</p>
        <Link to="/creator" className="ember-btn mt-6 inline-flex">Open Creator Studio</Link>
      </div>
    );
  }

  const canSubmit = videoCheck.phase === "ok" && poster && title && selectedGenres.length > 0;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12" data-testid="upload-movie-page">
      <Link to="/creator" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-8" data-testid="back-to-creator">
        <ArrowLeft className="h-4 w-4" /> Back to Studio
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Upload a movie</h1>
      <p className="text-white/50 mt-2">
        Videos upload directly to Cloudflare&apos;s global CDN — fast, resumable, and no size limit pain.
        All titles are reviewed by our team for Christian appropriateness before going live.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="upload-form">
        <div>
          <label className="text-eyebrow mb-2 block">Movie title</label>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark" placeholder="e.g. The Watchman" data-testid="upload-title-input" disabled={submitting} />
        </div>
        <div>
          <label className="text-eyebrow mb-2 block">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="input-dark resize-none" placeholder="A short synopsis of your story..." data-testid="upload-desc-input" disabled={submitting} />
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-eyebrow">Genres · pick up to {MAX_GENRES}</label>
            <span className="text-xs text-white/40" data-testid="upload-genre-counter">{selectedGenres.length} / {MAX_GENRES}</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="upload-genre-chips">
            {genreCatalog.length === 0 ? (
              <span className="text-sm text-white/50">Loading genres...</span>
            ) : (
              genreCatalog.map((g) => {
                const active = selectedGenres.includes(g.name);
                const capped = !active && selectedGenres.length >= MAX_GENRES;
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleGenre(g.name)}
                    disabled={submitting || capped}
                    className={`px-4 py-2 rounded-full border text-sm transition ${
                      active
                        ? "bg-[#E65F2B] border-[#E65F2B] text-white"
                        : capped
                        ? "border-white/5 text-white/20 cursor-not-allowed"
                        : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`upload-genre-chip-${g.slug}`}
                  >
                    {g.name}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {seriesList.length > 0 && (
          <div className="card-surface p-5 space-y-3" data-testid="series-picker-block">
            <p className="text-eyebrow text-[#EAB308]">Optional: attach as a series episode</p>
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
              <select
                value={seriesId}
                onChange={(e) => setSeriesId(e.target.value)}
                className="input-dark"
                disabled={submitting}
                data-testid="upload-series-select"
              >
                <option value="">— Standalone (not part of a series) —</option>
                {seriesList.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}</option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                value={season}
                onChange={(e) => setSeason(parseInt(e.target.value, 10) || 1)}
                className="input-dark"
                placeholder="Season"
                disabled={submitting || !seriesId}
                data-testid="upload-season-input"
              />
              <input
                type="number"
                min={1}
                value={episode}
                onChange={(e) => setEpisode(parseInt(e.target.value, 10) || 1)}
                className="input-dark"
                placeholder="Episode"
                disabled={submitting || !seriesId}
                data-testid="upload-episode-input"
              />
            </div>
            {seriesId && (
              <input
                value={episodeTitle}
                onChange={(e) => setEpisodeTitle(e.target.value)}
                className="input-dark"
                placeholder="Episode title (e.g. 'The Calling') — optional"
                maxLength={200}
                disabled={submitting}
                data-testid="upload-episode-title-input"
              />
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className={`card-surface p-6 cursor-pointer hover:border-[#E65F2B]/40 transition ${submitting ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-poster-zone">
            <ImageIcon className="h-6 w-6 text-[#EAB308] mb-2" />
            <p className="font-display font-bold">Poster image</p>
            <p className="text-xs text-white/50 mt-1">JPG / PNG, max {MAX_POSTER_MB}MB. 2:3 aspect ratio recommended. Or pick a frame from your video below.</p>
            {/* HTML5 `required` is bound to the input's own .files collection,
                which stays empty when we programmatically set `poster` from a
                video-frame click. Toggling required off once poster state is
                set lets the frame-based flow submit successfully. The JS
                guard in submit() + the canSubmit disabled state still catch
                the truly-empty case. */}
            <input type="file" accept="image/*" required={!poster} onChange={(e) => { const f = e.target.files?.[0] || null; setPoster(f); setPosterSource(f ? "uploaded" : ""); }} className="mt-4 text-xs w-full" data-testid="upload-poster-input" disabled={submitting} />
            {poster && (
              <p className="text-xs text-[#E65F2B] mt-2">
                ✓ {poster.name}{posterSource === "frame" && <span className="text-[#EAB308]"> · from video frame</span>}
              </p>
            )}
          </label>

          <label className={`card-surface p-6 cursor-pointer hover:border-[#E65F2B]/40 transition ${submitting ? "opacity-50 pointer-events-none" : ""}`} data-testid="upload-video-zone">
            <Cloud className="h-6 w-6 text-[#E65F2B] mb-2" />
            <p className="font-display font-bold">Movie file</p>
            <p className="text-xs text-white/50 mt-1">MP4 / MOV / WebM, up to {MAX_VIDEO_GB} GB · max 2 hours.</p>
            <input type="file" accept="video/*" required onChange={(e) => onVideoSelected(e.target.files?.[0] || null)} className="mt-4 text-xs w-full" data-testid="upload-video-input" disabled={submitting} />
            {video && videoCheck.phase === "checking" && (
              <p className="text-xs text-white/60 mt-2 flex items-center gap-1.5" data-testid="video-preflight-checking">
                <Loader2 className="h-3 w-3 animate-spin" /> Checking video...
              </p>
            )}
            {videoCheck.phase === "ok" && (
              <p className="text-xs text-green-400 mt-2 flex items-center gap-1.5" data-testid="video-preflight-ok">
                <Check className="h-3 w-3" /> {video.name} · ready
              </p>
            )}
            {videoCheck.phase === "error" && (
              <p className="text-xs text-red-400 mt-2 flex items-start gap-1.5" data-testid="video-preflight-error">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  {videoCheck.error}
                  {videoCheck.helpUrl && (
                    <>
                      {" "}
                      <a
                        href={videoCheck.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#EAB308] underline underline-offset-2 hover:no-underline inline-flex items-center gap-1"
                        data-testid="video-preflight-help-link"
                      >
                        See supported formats <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  )}
                </span>
              </p>
            )}
          </label>
        </div>

        {videoCheck.phase === "ok" && (
          <div className="card-surface p-5 flex flex-col sm:flex-row gap-5" data-testid="video-preflight-card">
            <div className="w-full sm:w-48 aspect-video bg-black rounded-lg overflow-hidden shrink-0">
              {videoCheck.thumbnailDataUrl ? (
                <img src={videoCheck.thumbnailDataUrl} alt="Video preview" className="w-full h-full object-cover" data-testid="video-preflight-thumbnail" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-white/30 text-xs">no preview</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-eyebrow text-green-400 mb-1 flex items-center gap-1.5"><Check className="h-3 w-3" /> Preflight passed</p>
              <p className="font-display font-bold text-base truncate">{video.name}</p>
              <div className="grid grid-cols-3 gap-4 mt-3 text-xs">
                <div>
                  <p className="text-white/30">Duration</p>
                  <p className="font-display text-sm font-bold text-white mt-0.5" data-testid="video-preflight-duration">{formatDuration(videoCheck.duration)}</p>
                </div>
                <div>
                  <p className="text-white/30">Resolution</p>
                  <p className="font-display text-sm font-bold text-white mt-0.5" data-testid="video-preflight-resolution">{videoCheck.width}×{videoCheck.height}</p>
                </div>
                <div>
                  <p className="text-white/30">Size</p>
                  <p className="font-display text-sm font-bold text-white mt-0.5">{(videoCheck.sizeBytes / 1024 / 1024).toFixed(1)} MB</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {videoCheck.phase === "ok" && videoCheck.frameSuggestions?.length > 0 && (
          <div className="card-surface p-5" data-testid="frame-poster-picker">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[#EAB308]" />
              <p className="text-eyebrow text-[#EAB308]">Use a frame as your poster</p>
            </div>
            <p className="text-xs text-white/50 mb-4">
              Skip the design work — pick a moment from your movie and we&apos;ll use that frame as the poster.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {videoCheck.frameSuggestions.map((f, i) => {
                const expectedName = `poster-from-${Math.round(f.time)}s.jpg`;
                const isActive = posterSource === "frame" && poster?.name === expectedName;
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => {
                      const file = dataUrlToFile(f.dataUrl, expectedName);
                      setPoster(file);
                      setPosterSource("frame");
                      toast.success(`Poster set from frame at ${formatDuration(f.time)}`);
                    }}
                    disabled={submitting}
                    data-testid={`poster-frame-${i}`}
                    className={`group relative aspect-video rounded-md overflow-hidden border-2 transition-all ${
                      isActive ? "border-[#E65F2B] ring-2 ring-[#E65F2B]/30" : "border-white/5 hover:border-white/30"
                    }`}
                  >
                    <img src={f.dataUrl} alt={`Frame at ${formatDuration(f.time)}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1">
                      <p className="text-[10px] font-bold text-white text-center tabular-nums">{formatDuration(f.time)}</p>
                    </div>
                    {isActive && (
                      <div className="absolute top-1 right-1 h-5 w-5 rounded-full bg-[#E65F2B] flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {submitting && (
          <div className="card-surface p-6" data-testid="upload-progress">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-white/70">
                {phase === "initializing" && "Preparing secure upload..."}
                {phase === "uploading" && "Uploading directly to Cloudflare..."}
                {phase === "processing" && "Cloudflare is transcoding — almost done!"}
              </span>
              <span className="text-[#E65F2B] font-bold">{progress}%</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#E65F2B] to-[#EAB308] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-xs text-white/40 mt-3">
              Resumable upload · If your connection drops, head back to Creator Studio to resume.
            </p>
            {phase === "uploading" && (
              <button type="button" onClick={cancelUpload} className="ghost-btn !py-2 !px-4 text-xs mt-4" data-testid="upload-cancel-button">
                Cancel
              </button>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !canSubmit}
          className="ember-btn w-full justify-center text-base disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="upload-submit-button"
        >
          <Upload className="h-5 w-5" />
          {submitting
            ? `${phase === "uploading" ? `Uploading ${progress}%` : phase.charAt(0).toUpperCase() + phase.slice(1)}...`
            : videoCheck.phase === "checking"
              ? "Checking video..."
              : videoCheck.phase === "error"
                ? "Fix video file to upload"
                : "Upload for review"}
        </button>
      </form>
    </div>
  );
}
